import React, { useState } from "react";
import { motion } from "motion/react";
import {
  IconArrowRight,
  IconCheck,
  IconListCheck,
  IconMail,
  IconPencil,
  IconPlus,
  IconSparkles,
  IconSparklesFilled,
  IconTarget,
  IconTrash,
  IconUser,
  IconX,
} from "@tabler/icons-react";
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  Grid,
  Group,
  Image,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { AVATARS, LANGUAGES, OBJECTIVES, TONES } from "./utils";
import LinkedinAccountSelector from "../../../../components/Selectors/LinkedinAccountSelector";
import EmailSignatureSelector from "../../../../components/Selectors/EmailSignatureSelector";
import EmailAccountSelector from "../../../../components/Selectors/EmailAccountSelector";

// --- COMPOSANTS DE WIDGETS INDIVIDUELS ---

function RecapWidget({ draft, onSubmit }) {
  const [role, setRole] = useState(draft.role);
  const [editing, setEditing] = useState(false);

  // Nouveaux états pour les cibles
  const [targets, setTargets] = useState(draft.targets || []);
  const [newTarget, setNewTarget] = useState("");

  // Fonction pour ajouter une cible
  const handleAddTarget = () => {
    const trimmed = newTarget.trim();
    if (trimmed && !targets.includes(trimmed)) {
      setTargets([...targets, trimmed]);
      setNewTarget("");
    }
  };

  // Fonction pour supprimer une cible
  const handleRemoveTarget = (targetToRemove) => {
    setTargets(targets.filter((t) => t !== targetToRemove));
  };

  return (
    <Stack gap="md">
      {/* Résumé */}
      <Paper p="md" radius="lg" bg="rgba(0, 0, 0, 0.03)" withBorder>
        <Text size="md" fw={600} tt="uppercase" c="dimmed">
          Résumé de l'entreprise
        </Text>
        <Text size="lg" mt={6}>
          {draft.companySummary}
        </Text>
      </Paper>

      {/* Rôle */}
      <Group gap="sm">
        <Text size="md" fw={600} tt="uppercase" c="dimmed" lts={1}>
          Rôle recommandé
        </Text>
        {editing ? (
          <TextInput
            value={role}
            autoFocus
            onChange={(e) => setRole(e.currentTarget.value)}
            onBlur={() => setEditing(false)}
            onKeyDown={(e) => e.key === "Enter" && setEditing(false)}
            size="md"
            radius="xl"
          />
        ) : (
          <Badge
            variant="light"
            color="primary"
            size="xl"
            radius="xl"
            style={{ cursor: "pointer", textTransform: "none" }}
            onClick={() => setEditing(true)}
            rightSection={<IconPencil size={12} />}
          >
            {role}
          </Badge>
        )}
      </Group>

      {/* Cibles */}
      <Box>
        <Text size="md" fw={600} tt="uppercase" c="dimmed" lts={1}>
          Cibles prioritaires détectées
        </Text>

        {/* Liste des cibles actuelles */}
        <Group gap="sm" mt="sm" wrap="wrap">
          {targets.map((t) => (
            <Tooltip key={t} label={t}>
              <Paper
                key={t}
                withBorder
                radius="xl"
                px="md"
                py={8}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  width: "fit-content", // C'est ici que la magie "free-width" opère
                  backgroundColor: "white",
                  borderColor: "var(--mantine-color-gray-3)",
                }}
              >
                <IconTarget
                  size={18}
                  color="var(--mantine-color-primary-6)"
                  stroke={2}
                />
                <Text size="sm" fw={600} c="dark.3" maw={250} truncate>
                  {t}
                </Text>
                <ActionIcon
                  size="sm"
                  color="gray"
                  variant="transparent"
                  onClick={() => handleRemoveTarget(t)}
                  style={{ cursor: "pointer", marginLeft: -4 }}
                >
                  <IconX size={14} />
                </ActionIcon>
              </Paper>
            </Tooltip>
          ))}
        </Group>

        {/* Champ pour ajouter une nouvelle cible */}
        <Group gap="sm" mt="md" wrap="nowrap">
          <TextInput
            placeholder="Ajouter une cible (ex: Directeurs RH...)"
            value={newTarget}
            onChange={(e) => setNewTarget(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                handleAddTarget();
              }
            }}
            size="lg"
            radius="xl"
            style={{ flex: 1 }}
          />
          <Button
            variant="light"
            radius="xl"
            size="md"
            onClick={handleAddTarget}
            disabled={!newTarget.trim()}
            leftSection={<IconPlus size={16} />}
          >
            Ajouter
          </Button>
        </Group>
      </Box>

      {/* Validation */}
      <Box mt="lg">
        <Button
          onClick={() =>
            onSubmit({ role, targets }, "C'est une bonne base, continuons !")
          }
          disabled={targets.length === 0}
          rightSection={<IconArrowRight size={16} />}
          radius="xl"
          size="md"
        >
          Parfait, continuons
        </Button>
      </Box>
    </Stack>
  );
}

function NameWidget({ draft, onSubmit }) {
  const [name, setName] = useState(draft.name);

  return (
    <Flex
      direction={{ base: "column", sm: "row" }}
      gap="sm"
      align={{ sm: "center" }}
    >
      <TextInput
        value={name}
        autoFocus
        onChange={(e) => setName(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (
            e.key === "Enter" &&
            !e.nativeEvent.isComposing &&
            e.keyCode !== 229 &&
            name.trim()
          ) {
            onSubmit({ name: name.trim() }, name.trim());
          }
        }}
        placeholder="Nom de l'agent"
        leftSection={
          <IconSparklesFilled
            size={16}
            color="var(--mantine-color-primary-6)"
          />
        }
        radius="xl"
        size="lg"
        style={{ flex: 1 }}
      />
      <Button
        disabled={!name.trim()}
        onClick={() => onSubmit({ name: name.trim() }, name.trim())}
        rightSection={<IconArrowRight size={16} />}
        radius="xl"
        size="md"
      >
        Valider le nom
      </Button>
    </Flex>
  );
}

function AvatarWidget({ onSubmit }) {
  return (
    <Grid gutter="sm">
      {AVATARS.map((a, i) => (
        <Grid.Col span={3} key={a.id}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.05 }}
          >
            <UnstyledButton
              onClick={() =>
                onSubmit({ avatarId: a.id }, `J'ai choisi l'avatar`)
              }
              style={{
                position: "relative",
                aspectRatio: "1/1",
                borderRadius: "var(--mantine-radius-xl)",
                overflow: "hidden",
                border: "2px solid transparent",
                backgroundColor: "var(--mantine-color-gray-1)",
                transition: "all 0.2s ease",
                display: "block",
                width: "100%",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.borderColor = `var(--mantine-color-${a.accent}-5)`)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.borderColor = "transparent")
              }
            >
              <Image
                src={a.src || "/placeholder.svg"}
                alt={a.name}
                w="100%"
                h="100%"
                fit="cover"
              />
            </UnstyledButton>
          </motion.div>
        </Grid.Col>
      ))}
    </Grid>
  );
}

function GenderWidget({ onSubmit }) {
  const options = [
    { id: "female", label: "Féminin" },
    { id: "male", label: "Masculin" },
    { id: "neutral", label: "Neutre" },
  ];
  return (
    <Group gap="sm">
      {options.map((o) => (
        <Button
          key={o.id}
          variant="default"
          radius="xl"
          size="md"
          onClick={() => onSubmit({ gender: o.id }, o.label)}
          style={(theme) => ({
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: theme.colors.primary[5],
              backgroundColor: theme.colors.primary[0],
              color: theme.colors.primary[7],
            },
          })}
          disabled={o.id === "neutral"} // Désactiver le bouton "Neutre"
        >
          {o.label}
        </Button>
      ))}
    </Group>
  );
}

function LanguageWidget({ draft, onSubmit }) {
  const [sel, setSel] = useState(draft.language);

  // Utilisation du composant Select natif de Mantine avec rendu personnalisé
  return (
    <Flex
      direction={{ base: "column", sm: "row" }}
      gap="sm"
      align={{ sm: "center" }}
    >
      <Box style={{ flex: 1 }}>
        <Select
          value={sel}
          onChange={setSel}
          data={LANGUAGES.map((l) => ({ value: l.label, label: l.label }))}
          radius="xl"
          size="lg"
          renderOption={({ option }) => {
            const l = LANGUAGES.find((lang) => lang.label === option.value);
            return (
              <Group gap="sm" wrap="nowrap">
                <Badge size="md" variant="light" color="primary">
                  {l.code}
                </Badge>
                <Box>
                  <Text size="md" fw={500}>
                    {l.label}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {l.region}
                  </Text>
                </Box>
              </Group>
            );
          }}
        />
      </Box>
      <Button
        onClick={() => onSubmit({ language: sel }, sel)}
        rightSection={<IconArrowRight size={16} />}
        radius="xl"
        size="md"
      >
        Confirmer la langue
      </Button>
    </Flex>
  );
}

function IntegrationWidget({ draft, onSubmit }) {
  // On stocke l'objet complet renvoyé par le onChange pour pouvoir
  // récupérer le nom (ou l'email) lors de la génération du résumé dans le chat.
  const [emailSender, setEmailSender] = useState(null);
  const [signature, setSignature] = useState(null);
  const [linkedinSender, setLinkedinSender] = useState(null);

  // Validation : on exige au moins un canal d'envoi (soit un Email, soit un LinkedIn)
  const isValid = emailSender?.id || linkedinSender?.id;

  const handleSubmit = (ignore) => {
    // 1. On prépare le "patch" pour mettre à jour le draft global
    const patch = {
      integration_id: emailSender?.id || null,
      email_signature_id: signature?.id || null,
      linkedin_integration_id: linkedinSender?.id || null,
    };

    if (ignore) {
      const summaryText = "Ignorer pour le moment";

      // 3. On envoie les données
      onSubmit(patch, summaryText);
    }
    if (!isValid) return;

    // 2. On construit une phrase de résumé personnalisée pour la bulle de chat
    const summaryParts = [];
    if (emailSender) {
      // Adaptez ".name" ou ".email" selon ce que contient votre objet sender
      summaryParts.push(
        `Email : ${emailSender.name || emailSender.email || "Configuré"}`,
      );
    }
    if (signature) {
      summaryParts.push(`Signature : ${signature.name || "Configurée"}`);
    }
    if (linkedinSender) {
      summaryParts.push(`LinkedIn : ${linkedinSender.name || "Configuré"}`);
    }

    const summaryText = (
      <>
        J'ai connecté ces comptes : <br />
        {summaryParts.map((part, index) => (
          <React.Fragment key={index}>
            • {part} {index < summaryParts.length - 1 ? <br /> : ""}
          </React.Fragment>
        ))}
      </>
    );

    // 3. On envoie les données
    onSubmit(patch, summaryText);
  };

  return (
    <Stack gap="md">
      <Grid gutter="md">
        {/* LIGNE 1 : Email & Signature */}
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <EmailAccountSelector
            label="Compte Email"
            size="md"
            w="100%"
            value={emailSender?.id}
            onChange={setEmailSender}
            radius="lg"
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <EmailSignatureSelector
            label="Signature (Optionnelle)"
            size="md"
            w="100%"
            value={signature?.id}
            onChange={setSignature}
            selectorProps={{ radius: "lg" }}
          />
        </Grid.Col>

        {/* LIGNE 2 : LinkedIn */}
        <Grid.Col span={12}>
          <LinkedinAccountSelector
            label="Compte LinkedIn"
            size="md"
            w="100%"
            value={linkedinSender?.id}
            onChange={setLinkedinSender}
            other={{ radius: "lg" }}
          />
        </Grid.Col>
      </Grid>

      <Group mt="xs">
        <Button
          disabled={!isValid}
          onClick={handleSubmit}
          rightSection={<IconArrowRight size={16} />}
          radius="xl"
          size="md"
        >
          Valider les intégrations
        </Button>
        <Button
          onClick={() => handleSubmit(true)}
          radius="xl"
          size="md"
          variant="outline"
        >
          Passer cette étape pour l'instant
        </Button>
      </Group>
    </Stack>
  );
}

function ValuePropsWidget({ draft, onSubmit }) {
  // 1. L'état est maintenant un tableau vide par défaut
  const [selected, setSelected] = useState([]);

  // 2. Fonction pour cocher / décocher un élément
  const toggleSelection = (index) => {
    if (selected.includes(index)) {
      // S'il est déjà sélectionné, on le retire
      setSelected(selected.filter((i) => i !== index));
    } else {
      // Sinon, on l'ajoute
      setSelected([...selected, index]);
    }
  };

  return (
    <Stack gap="md">
      <Stack gap="xs">
        {draft.valueProps.map((vp, i) => {
          // 3. On vérifie si l'index actuel est dans le tableau des sélections
          const active = selected.includes(i);

          return (
            <UnstyledButton
              key={i}
              onClick={() => toggleSelection(i)}
              style={(theme) => ({
                padding: theme.spacing.md,
                borderRadius: theme.radius.lg,
                border: `1px solid ${active ? theme.colors.primary[5] : theme.colors.gray[3]}`,
                backgroundColor: active ? theme.colors.primary[0] : theme.white,
                transition: "all 0.2s ease",
                display: "flex",
                gap: theme.spacing.md,
                alignItems: "flex-start",
              })}
            >
              <Box
                w={24}
                h={24}
                style={(theme) => ({
                  borderRadius: 6, // Remplacé "50%" par 6 pour faire un effet "Checkbox" multiple
                  border: `1px solid ${active ? "transparent" : theme.colors.gray[4]}`,
                  backgroundColor: active
                    ? theme.colors.primary[6]
                    : "transparent",
                  color: theme.white,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  marginTop: 2,
                  transition: "all 0.2s ease",
                })}
              >
                {active && <IconCheck size={14} />}
              </Box>
              <Text size="md" lh={1.5}>
                {vp}
              </Text>
            </UnstyledButton>
          );
        })}
      </Stack>
      <Box>
        <Button
          disabled={selected.length === 0} // Désactivé si rien n'est sélectionné
          onClick={() => {
            if (selected.length > 0) {
              // On récupère toutes les promesses sélectionnées
              const chosenProps = selected.map((i) => draft.valueProps[i]);

              // On formate le message envoyé dans le chat en fonction du nombre
              const summaryText =
                chosenProps.length === 1 ? (
                  chosenProps[0]
                ) : (
                  <>
                    J'ai choisi ces promesses : <br />
                    {chosenProps.map((prop, i) => (
                      <React.Fragment key={i}>
                        • {prop} {i < chosenProps.length - 1 ? <br /> : ""}
                      </React.Fragment>
                    ))}
                  </>
                );

              onSubmit(
                // On joint les choix avec un saut de ligne pour les stocker dans le draft
                { chosenValueProp: chosenProps.join("\n") },
                summaryText,
              );
            }
          }}
          rightSection={<IconArrowRight size={16} />}
          radius="xl"
          size="md"
        >
          {selected.length > 1
            ? "Choisir ces promesses"
            : "Choisir cette promesse"}
        </Button>
      </Box>
    </Stack>
  );
}

function ToneWidget({ draft, onSubmit }) {
  return (
    <Grid gutter="sm">
      {TONES.map((t) => {
        // On vérifie si draft.tone correspond au label (venant de l'API initiale)
        // OU s'il correspond au prompt (si l'utilisateur a déjà cliqué sur le bouton)
        const recommended = t.label === draft.tone || t.prompt === draft.tone;

        return (
          <Grid.Col span={{ base: 12, sm: 6 }} key={t.id}>
            <UnstyledButton
              // 🔴 C'est ici qu'on "patch" le draft avec le prompt complet
              // tout en gardant le label court pour le résumé du chat
              onClick={() => onSubmit({ tone: t.prompt }, t.label)}
              style={(theme) => ({
                position: "relative",
                display: "block",
                width: "100%",
                padding: theme.spacing.md,
                borderRadius: theme.radius.xl,
                border: `1px solid ${recommended ? theme.colors.primary[5] : theme.colors.gray[3]}`,
                backgroundColor: theme.white,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: theme.colors.primary[4],
                  transform: "translateY(-2px)",
                  boxShadow: theme.shadows.sm,
                },
              })}
            >
              {recommended && (
                <Badge
                  variant="light"
                  color="primary"
                  size="md"
                  radius="sm"
                  pos="absolute"
                  top={12}
                  right={12}
                >
                  Recommandé
                </Badge>
              )}
              <Text size="md" fw={600} ml={8} pr={recommended ? 80 : 0}>
                {t.label}
              </Text>
              <Text size="sm" c="dimmed" ml={8} mt={4} lh={1.5}>
                {t.description}
              </Text>
            </UnstyledButton>
          </Grid.Col>
        );
      })}
    </Grid>
  );
}

function ObjectiveWidget({ draft, onSubmit, onSkip }) {
  // État pour stocker l'ID de l'objectif sélectionné
  const [selected, setSelected] = useState(draft?.main_objective || null);
  // État pour stocker le lien ou le texte saisi
  const [inputValue, setInputValue] = useState(draft?.objective_link || "");

  const selectedOption = OBJECTIVES.find((o) => o.id === selected);

  // Vérifie si le bouton de validation doit être désactivé
  const isSubmitDisabled = 
    !selected || 
    (selectedOption?.requiresInput && !inputValue.trim());

  const handleSubmit = () => {
    if (isSubmitDisabled) return;

    // 2. Formatage du payload pour l'API
    const payload = {
      main_objective: selectedOption.main_objective,
      objective_link: selectedOption.requiresInput ? inputValue.trim() : null,
      objective_link_type: selectedOption.objective_link_type,
    };

    // Formatage du message pour le résumé du chat
    let summaryText = selectedOption.label;
    if (selectedOption.requiresInput && inputValue.trim()) {
      summaryText += ` (${inputValue.trim()})`;
    }

    onSubmit(payload, summaryText);
  };

  return (
    <Stack gap="md">
      <Stack gap="xs">
        {OBJECTIVES.map((obj) => {
          const active = selected === obj.id;

          return (
            <UnstyledButton
              key={obj.id}
              onClick={() => {
                setSelected(obj.id);
                // On réinitialise l'input si on change d'option
                if (!active) setInputValue("");
              }}
              style={(theme) => ({
                padding: theme.spacing.md,
                borderRadius: theme.radius.lg,
                border: `1px solid ${
                  active ? theme.colors.primary[5] : theme.colors.gray[3]
                }`,
                backgroundColor: active ? theme.colors.primary[0] : theme.white,
                transition: "all 0.2s ease",
                display: "flex",
                flexDirection: "column", // Permet d'empiler le texte et l'input
                gap: theme.spacing.sm,
              })}
            >
              <Group wrap="nowrap" align="flex-start" gap="md" w="100%">
                {/* Icône de Checkbox */}
                <Box
                  w={24}
                  h={24}
                  style={(theme) => ({
                    borderRadius: 6,
                    border: `1px solid ${
                      active ? "transparent" : theme.colors.gray[4]
                    }`,
                    backgroundColor: active
                      ? theme.colors.primary[6]
                      : "transparent",
                    color: theme.white,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    marginTop: 2,
                    transition: "all 0.2s ease",
                  })}
                >
                  {active && <IconCheck size={14} />}
                </Box>
                
                <Text size="md" lh={1.5} fw={active ? 500 : 400}>
                  {obj.label}
                </Text>
              </Group>

              {/* Champ texte dynamique qui s'ouvre en douceur */}
              <Collapse in={active && obj.requiresInput}>
                <Box pl={40} w="100%" onClick={(e) => e.stopPropagation()}>
                  <TextInput
                    placeholder={obj.inputPlaceholder}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !isSubmitDisabled) {
                        handleSubmit();
                      }
                    }}
                    radius="md"
                    size="sm"
                    autoFocus
                  />
                </Box>
              </Collapse>
            </UnstyledButton>
          );
        })}
      </Stack>

      <Group gap="lg" mt="sm">
        <Button
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          rightSection={<IconArrowRight size={16} />}
          radius="xl"
          size="md"
        >
          Choisir cet objectif
        </Button>
        
        {/* Lien pour passer l'étape, comme sur l'image */}
        <Anchor
          component="button"
          type="button"
          c="dimmed"
          size="sm"
          underline="always"
          onClick={() => {
            if (onSkip) onSkip();
            else onSubmit({ main_objective: null, objective_link: null, objective_link_type: null }, "Passer cette étape");
          }}
        >
          Passer cette étape
        </Anchor>
      </Group>
    </Stack>
  );
}

function InstructionsWidget({ draft, onSubmit }) {
  // On transforme le texte brut en un tableau de consignes
  const [instructions, setInstructions] = useState(() => {
    if (!draft?.instructions) return [""];
    if (Array.isArray(draft.instructions)) return draft.instructions;
    return draft.instructions.split("\n").filter((s) => s.trim().length > 0);
  });

  // Mettre à jour une ligne
  const handleUpdate = (index, value) => {
    const newInst = [...instructions];
    newInst[index] = value;
    setInstructions(newInst);
  };

  // Supprimer une ligne
  const handleDelete = (index) => {
    setInstructions(instructions.filter((_, i) => i !== index));
  };

  // Ajouter une ligne vide
  const handleAdd = () => {
    setInstructions([...instructions, ""]);
  };

  // Soumission
  const handleSubmit = () => {
    // On nettoie les lignes vides avant d'envoyer
    const finalInstructions = instructions
      .map((i) => i.trim())
      .filter((i) => i.length > 0);

    // On recompose la chaîne avec des sauts de ligne pour l'API
    onSubmit(
      { instructions: finalInstructions.join("\n") },
      "Ces consignes me conviennent",
    );
  };

  // Le bouton valider est actif s'il y a au moins une consigne non vide
  const isValid = instructions.some((i) => i.trim().length > 0);

  return (
    <Stack gap="md">
      {/* BANNIÈRE D'INFORMATION */}
      <Paper
        withBorder
        p="md"
        radius="xl"
        bg="violet.0"
        style={{ borderColor: "var(--mantine-color-violet-2)" }}
      >
        <Group wrap="nowrap">
          <IconListCheck color="var(--mantine-color-violet-5)" size={24} />
          <Text size="md" c="dark.3" lh={1.5}>
            Ces consignes pilotent le comportement de votre agent. <br /> Vous pouvez
            en{" "}
            <Text component="span" c="violet.5" fw={600}>
              supprimer
            </Text>{" "}
            ou en{" "}
            <Text component="span" c="violet.5" fw={600}>
              ajouter
            </Text>{" "}
            avant de le créer.
          </Text>
        </Group>
      </Paper>

      {/* LISTE DES CONSIGNES */}
      <Stack gap="sm">
        {instructions.map((inst, index) => (
          <Paper
            key={index}
            withBorder
            px="md"
            radius="xl"
            style={{ borderColor: "var(--mantine-color-gray-3)" }}
          >
            <Group wrap="nowrap">
              {/* Numérotation */}
              <ThemeIcon
                size={32}
                radius="xl"
                color="violet.0"
                c="violet.6"
                style={{ flexShrink: 0, marginTop: 4 }}
              >
                <Text fw={700} size="lg">
                  {index + 1}
                </Text>
              </ThemeIcon>

              {/* Champ d'édition invisible */}
              <Textarea
                variant="unstyled"
                autosize
                minRows={1}
                value={inst}
                onChange={(e) => handleUpdate(index, e.currentTarget.value)}
                placeholder="Écrivez votre consigne ici..."
                style={{ flex: 1 }}
                styles={{
                  input: {
                    padding: 8,
                    lineHeight: 1.5,
                    fontSize: "12px",
                    color: "var(--mantine-color-dark-3)",
                    border: 0
                  },
                }}
              />

              {/* Bouton de suppression */}
              <ActionIcon
                color="gray"
                variant="subtle"
                onClick={() => handleDelete(index)}
                radius="xl"
                size="lg"
                style={{ flexShrink: 0, marginTop: 2 }}
              >
                <IconTrash size={18} stroke={1.5} />
              </ActionIcon>
            </Group>
          </Paper>
        ))}
      </Stack>

      {/* BOUTON AJOUTER */}
      <Button
        variant="outline"
        color="violet.3"
        c="violet.6"
        radius="xl"
        size="md"
        onClick={handleAdd}
        style={{ borderWidth: 1 }}
        fullWidth
      >
        + Ajouter une consigne
      </Button>

      {/* BOUTON VALIDER */}
      <Box mt="sm">
        <Button
          disabled={!isValid}
          onClick={handleSubmit}
          rightSection={<IconArrowRight size={16} />}
          radius="xl"
          size="md"
        >
          Valider les consignes
        </Button>
      </Box>
    </Stack>
  );
}

// --- ORCHESTRATEUR PRINCIPAL ---

export function ChatWidget(props) {
  switch (props.kind) {
    case "recap":
      return <RecapWidget {...props} />;
    case "valueProps":
      return <ValuePropsWidget {...props} />;
    case "name":
      return <NameWidget {...props} />;
    case "avatar":
      return <AvatarWidget {...props} />;
    case "gender":
      return <GenderWidget {...props} />;
    case "language":
      return <LanguageWidget {...props} />;
    case "integration":
      return <IntegrationWidget {...props} />;
    case "tone":
      return <ToneWidget {...props} />;
    case "objective":
      return <ObjectiveWidget {...props} />;
    case "instructions":
      return <InstructionsWidget {...props} />;
    default:
      return null;
  }
}
